<template>
  <div>
    <b-container>
      <b-card>
        <b-row slot="header" align-v="center">
          <b-col>
            <h4 class="card-title">{{ $t("message.addBooking") }}</h4>
          </b-col>
          <b-col class="text-right">
            <n-button
              variant="secondary"
              role="navigation"
              simple
              @click.native="backToBookings"
            >
              {{ $t("message.cancel") }}
            </n-button>
          </b-col>
        </b-row>
        <div>
          <b-row>
            <b-col md="3">
              <h6>{{ $t("message.employee") }}</h6>
            </b-col>
            <b-col md="6">
              <avatar
                v-for="employee in employeesList"
                size="100"
                class="p-3"
                :key="employee.name"
                :src="employee.image"
                :text="employee.name"
                :active="model.resourceId === employee._id"
                button
                @click="onEmployeeClick(employee)"
              ></avatar>
            </b-col>
          </b-row>
          <section v-if="canShowServiceSection" id="booking__serviceSection">
            <hr />
            <b-row>
              <b-col md="3">
                <h6>Service</h6>
              </b-col>
              <b-col md="6">
                <n-button
                  v-for="service in serviceOptions"
                  type="info"
                  :key="service._id"
                  :simple="model.serviceId !== service._id"
                  @click.native="onServiceClick(service)"
                  block
                  >{{ service.name }}</n-button
                >
              </b-col>
            </b-row>
          </section>
          <section
            v-if="canShowServicePackageSection"
            id="booking__servicePackageSection"
          >
            <hr />
            <b-row>
              <b-col md="3">
                <h6>Package</h6>
              </b-col>
              <b-col md="6">
                <n-button
                  v-for="servicePackage in servicePackageOptions"
                  type="info"
                  :key="servicePackage._id"
                  :simple="model.servicePackageId !== servicePackage._id"
                  @click.native="onServicePackageClick(servicePackage)"
                  block
                >
                  {{ servicePackage.name }} €{{ servicePackage.price }}
                </n-button>
              </b-col>
            </b-row>
          </section>
          <section v-if="canShowDateSection" id="booking__dateSection">
            <hr />
            <b-row>
              <b-col md="3">
                <h6>{{ $t("message.date") }}</h6>
              </b-col>
              <b-col md="6">
                <div v-show="isLoadingDates" class="text-center">
                  <b-spinner variant="primary" show></b-spinner>
                </div>
                <b-alert
                  variant="warning"
                  :show="!isLoadingDates && !availableDates.length"
                >
                  {{ selectedEmployeeName }} is fully booked.
                </b-alert>
                <b-alert
                  variant="warning"
                  :show="isPreselectedDateNotAvailableWarningVisible"
                >
                  {{ selectedEmployeeName }} is not available on
                  {{ preselectedDateFormatted }}, please select another date.
                </b-alert>
                <div v-show="availableDates.length">
                  <div class="text-right">
                    <div
                      class="btn-group"
                      role="navigation"
                      aria-label="Switch view type for date options"
                    >
                      <n-button
                        type="primary"
                        native-type="button"
                        @click.native="dateView = DATE_VIEWS.LIST"
                        :simple="dateView !== DATE_VIEWS.LIST"
                      >
                        <i
                          class="fa fw fa-list-ul"
                          aria-hidden="true"
                          title="Booking Date List View"
                        />
                      </n-button>
                      <n-button
                        type="primary"
                        native-type="button"
                        @click.native="dateView = DATE_VIEWS.CALENDAR"
                        :simple="dateView !== DATE_VIEWS.CALENDAR"
                      >
                        <i
                          class="fa fw fa-calendar"
                          aria-hidden="true"
                          title="Booking Date Calendar View"
                        />
                      </n-button>
                    </div>
                  </div>
                  <div
                    v-show="dateView === DATE_VIEWS.LIST"
                    div
                    class="text-center"
                  >
                    <n-button
                      v-for="date in listDateOptions"
                      class="mt-1 mb-0"
                      :key="date.value"
                      type="info"
                      :simple="selectedDate !== date.value"
                      @click.native="onDateClick(date.value)"
                      block
                    >
                      {{ date.text }}
                    </n-button>
                    <n-button
                      v-show="numListDatesToShow < availableDates.length"
                      type="info"
                      link
                      @click.native="showMoreDates()"
                    >
                      Show More
                    </n-button>
                  </div>
                  <div v-show="dateView === DATE_VIEWS.CALENDAR">
                    <VueCtkDateTimePicker
                      formatted="ddd D MMM YYYY"
                      :enabled-dates="availableDates"
                      :value="model.start"
                      @input="onDateClick"
                      only-date
                      right
                      no-label
                      no-shortcuts
                      no-button-now
                      no-clear-button
                      no-header
                      no-keyboard
                      auto-close
                      inline
                    ></VueCtkDateTimePicker>
                  </div>
                </div>
              </b-col>
            </b-row>
          </section>
          <section v-if="canShowTimeSection" id="booking__TimeSection">
            <hr />
            <b-row>
              <b-col md="2">
                <h6>Time</h6>
              </b-col>
              <b-col md="8" class="mb-2">
                <b-alert variant="info" :show="!timeOptions.length">
                  {{ selectedEmployeeName }} has no available times on
                  <b>{{ selectedDateFormatted }}</b
                  >.
                </b-alert>
                <b-row>
                  <b-col xs="12" sm="4" class="text-center">
                    <p>
                      <small><strong>MORNING</strong></small>
                    </p>
                    <small
                      class="text-secondary"
                      v-show="!timeOptionsMorning.length"
                    >
                      Fully booked
                    </small>
                    <n-button
                      v-for="time in timeOptionsMorning"
                      class="mt-1 mb-0"
                      :key="time.value"
                      type="info"
                      :simple="selectedTime !== time.value"
                      @click.native="onTimeClick(time.value)"
                      block
                    >
                      {{ time.text }}
                    </n-button>
                  </b-col>
                  <b-col xs="12" sm="4" class="text-center">
                    <p>
                      <small><strong>AFTERNOON</strong></small>
                    </p>
                    <small
                      class="text-secondary"
                      v-show="!timeOptionsAfternoon.length"
                    >
                      Fully booked
                    </small>
                    <n-button
                      v-for="time in timeOptionsAfternoon"
                      class="mt-1 mb-0"
                      :key="time.value"
                      type="info"
                      :simple="selectedTime !== time.value"
                      @click.native="onTimeClick(time.value)"
                      block
                    >
                      {{ time.text }}
                    </n-button>
                  </b-col>
                  <b-col xs="12" sm="4" class="text-center">
                    <p>
                      <small><strong>EVENING</strong></small>
                    </p>
                    <small
                      class="text-secondary"
                      v-show="!timeOptionsEvening.length"
                    >
                      Fully booked
                    </small>
                    <n-button
                      v-for="time in timeOptionsEvening"
                      class="mt-1 mb-0"
                      :key="time.value"
                      type="info"
                      :simple="selectedTime !== time.value"
                      @click.native="onTimeClick(time.value)"
                      block
                    >
                      {{ time.text }}
                    </n-button>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </section>
          <section v-if="canContinueToConfirmation" id="booking__endSection">
            <hr />
            <b-row>
              <b-col md="6" offset-md="3">
                <n-button
                  ref="continueButton"
                  type="info"
                  block
                  autofocus
                  @click.native="showConfirmationModal"
                >
                  {{ $t("message.continue") }}
                </n-button>
              </b-col>
            </b-row>
          </section>
        </div>
      </b-card>
    </b-container>
    <modal :show.sync="isConfirmationModalVisible">
      <h4 slot="header" class="title title-up">
        {{ $t("message.confirmBooking") }}
      </h4>
      <div>
        <ValidationObserver v-slot="{ handleSubmit }">
          <div v-if="selectedEmployee" class="d-flex justify-content-between">
            <span class="text-muted">{{ $t("message.employee") }}</span>
            <strong>{{ selectedEmployee.name }}</strong>
          </div>
          <hr class="mt-1 mb-1" />
          <div v-if="selectedService" class="d-flex justify-content-between">
            <span class="text-muted">{{ $t("message.service") }}</span>
            <strong>{{ selectedService.name }}</strong>
          </div>
          <hr class="mt-1 mb-1" />
          <div
            v-if="selectedServicePackage"
            class="d-flex justify-content-between"
          >
            <span class="text-muted">{{ $t("message.package") }}</span>
            <strong>{{ selectedServicePackage.name }}</strong>
          </div>
          <hr class="mt-1 mb-1" />
          <div
            v-if="selectedDateFormatted"
            class="d-flex justify-content-between"
          >
            <span class="text-muted">{{ $t("message.date") }}</span>
            <strong>{{ selectedDateFormatted }}</strong>
          </div>
          <hr class="mt-1 mb-1" />
          <div
            v-if="selectedTimeFormatted"
            class="d-flex justify-content-between"
          >
            <span class="text-muted">{{ $t("message.time") }}</span>
            <strong>{{ selectedTimeFormatted }}</strong>
          </div>
          <hr class="mt-1 mb-1" />
          <div class="d-flex justify-content-between">
            <span class="text-muted">{{ $t("message.total") }}</span>
            <strong>{{ totalFormatted }}</strong>
          </div>
          <hr class="mt-1 mb-1" />
          <br />
            <h6>Payment Status</h6>
            <fg-input>
              <b-form-select
                v-model="model.paymentStatus"
                :options="paymentStatusOptions"
              >
              </b-form-select>
            </fg-input>
          <br />
          <h6>{{ $t("message.bookingCustomerDetails") }}</h6>
          <b-form @submit.prevent="handleSubmit(submit)">
            <ValidationProvider rules="required|min:1" v-slot="{ errors }">
              <fg-input
                v-model="model.customer.name"
                key="customer-name-input"
                :placeholder="$t('message.bookingCustomerNamePlaceholder')"
                :error="errors[0]"
                autofocus
              />
            </ValidationProvider>
            <ValidationProvider rules="required" v-slot="{ errors }">
              <fg-input
                type="tel"
                v-model="model.customer.phoneNumber"
                :error="errors[0]"
                :placeholder="
                  $t('message.bookingCustomerPhoneNumberPlaceholder')
                "
              />
            </ValidationProvider>
            <ValidationProvider rules="email" v-slot="{ errors }">
              <fg-input
                type="email"
                v-model="model.customer.emailAddress"
                :placeholder="$t('message.bookingCustomerEmailPlaceholder')"
                :error="errors[0]"
              />
            </ValidationProvider>
            <br />
            <h6>Notes</h6>
            <fg-input>
              <b-form-textarea
                v-model="model.notes"
                :placeholder="$t('message.bookingNotesPlaceholder')"
              ></b-form-textarea>
            </fg-input>
            <br />
            <n-button
              class="mb-0"
              type="info"
              block
              native-type="submit"
              :busy="isSaving"
              :disabled="isSaving"
            >
              {{ $t("message.confirm") }}
            </n-button>
          </b-form>
        </ValidationObserver>
      </div>
      <div slot="footer"></div>
    </modal>
  </div>
</template>
<script>
import moment from "moment";
import _ from "lodash";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { mapActions, mapGetters } from "vuex";

import bookings, { DEFAULT_ADMIN_BOOKING, paymentStatusTypes } from "@/store/bookings";
import { goBack } from "@/mixins";
import { Avatar, Modal, Checkbox } from "@/components";
import * as Time from "@/helpers/time";
import { db, docFromSnapshot } from '@/firebase';
import { DURATION_UNITS } from "@/views/management/services/services.lib";

const DATE_VIEWS = {
  LIST: "LIST",
  CALENDAR: "CALENDAR"
};

export default {
  mixins: [goBack],
  components: {
    ValidationObserver,
    ValidationProvider,
    [Checkbox.name]: Checkbox,
    Avatar,
    Modal
  },
  data() {
    return {
      model: _.cloneDeep(DEFAULT_ADMIN_BOOKING),
      date: null,
      preselectedDate: null,
      isPreselectedDateNotAvailableWarningVisible: false,
      time: null,
      isLoading: false,
      isConfirmationModalVisible: false,
      isSaving: false,
      isLoadingDates: false,
      numListDatesToShow: 7,
      DATE_VIEWS,
      dateView: DATE_VIEWS.LIST,
      paymentStatusOptions: [
        {
          text: 'Not paid',
          value: paymentStatusTypes.NOT_PAID
        },
        {
          text: 'Paid',
          value: paymentStatusTypes.PAID
        }
      ]
    };
  },
  methods: {
    ...mapActions({
      fetchEmployees: "employees/fetchEmployees",
      fetchAllWithAnyEmployee: "employees/fetchAllWithAnyEmployee",
      fetchAnyEmployee: "employees/fetchAnyEmployee",
      fetchServices: "services/fetchServices",
      fetchAvailableTimes: "bookings/fetchAvailableTimes",
      createAdminBooking: "bookings/createAdminBooking"
    }),
    showLoadingIndicator() {
      this.isLoading = true;
    },
    hideLoadingIndicator() {
      this.isLoading = false;
    },
    async populateWith(bookingId) {
      if (!bookingId) return;

      const snapshot = await db
        .collection("bookings")
        .doc(bookingId)
        .get();

      const booking = docFromSnapshot(snapshot);
      if (!booking) {
        this.$notify({
          type: "warning",
          title: this.$t("message.warning"),
          text: "The original booking could not be found."
        });
        return;
      }

      const employee = this.employees[booking.resourceId];
      const service = this.services[booking.serviceId];

      if (booking.customer) {
        this.model.customer = { ...booking.customer };
      }

      if (!employee || !service) {
        this.$notify({
          type: "warning",
          title: this.$t("message.warning"),
          text: "Some booking details are no longer available and could not be pre-filled."
        });
        return;
      }

      const servicePackage =
        service.packages && service.packages[booking.servicePackageId];
      if (!servicePackage) {
        this.onEmployeeClick(employee);
        this.onServiceClick(service);
        this.$notify({
          type: "warning",
          title: this.$t("message.warning"),
          text: "The service package for this booking is no longer available."
        });
        return;
      }

      this.onEmployeeClick(employee);
      this.onServiceClick(service);
      this.onServicePackageClick(servicePackage);
    },
    onEmployeeClick(employee) {
      if (!employee || !employee._id) return;
      this.model.resourceId = employee._id;
      // TODO check if employee can carry out already selected service
      this.resetService();
    },
    onServiceClick({ _id }) {
      this.model.serviceId = _id;
      this.resetPackage();
    },
    onServicePackageClick({ _id, price }) {
      if (this.model.servicePackageId === _id) return;
      // TODO reset date and/or time if slot not available for package
      this.model = {
        ...this.model,
        servicePackageId: _id,
        total: price,
        start: null,
        end: null
      };

      this.doFetchAvailableTimes();
    },
    async doFetchAvailableTimes() {
      const { resourceId, serviceId } = this.model;
      if (!resourceId || !serviceId) return [];

      this.showDatesLoadingIndicator();

      try {
        await this.fetchAvailableTimes({ resourceId, serviceId });
        this.handlePreselectedDate();
      } catch ({ message }) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text: message
        });
      }

      this.hideDatesLoadingIndicator();
    },
    showDatesLoadingIndicator() {
      this.isLoadingDates = true;
    },
    hideDatesLoadingIndicator() {
      this.isLoadingDates = false;
    },
    handlePreselectedDate() {
      const { preselectedDate } = this;
      if (!preselectedDate) return;
      if (this.availableDates.includes(preselectedDate)) {
        // Wall-clock GMT midnight — matches onDateClick / backend slot encoding
        this.model.start = moment
          .tz(preselectedDate, Time.DATE_FORMAT_CODE, "GMT")
          .format();
      } else {
        this.isPreselectedDateNotAvailableWarningVisible = true;
      }
    },
    resetService() {
      this.model.serviceId = null;
      this.resetPackage();
    },
    resetPackage() {
      this.model.servicePackageId = null;
      this.resetDateTimes();
      this.resetTotal();
    },
    resetTotal() {
      this.model.total = null;
    },
    showMoreDates() {
      this.numListDatesToShow += 7;
    },
    onDateClick(date) {
      this.model.start = moment.tz(date, Time.DATE_FORMAT_CODE, "GMT").format();
      this.model.end = null;
      this.isPreselectedDateNotAvailableWarningVisible = false;
    },
    resetDateTimes() {
      this.model.start = null;
      this.model.end = null;
    },
    onTimeClick(time) {
      // Encode selected wall-clock slot as GMT on the wire (same as online booking
      // and Cloud Functions moment(booking.start).format("HH:mm")).
      const dateStr = this.model.start
        ? moment.utc(this.model.start).format(Time.DATE_FORMAT_CODE)
        : null;
      if (!dateStr) return;

      this.model.start = moment
        .utc(
          `${dateStr} ${time}`,
          `${Time.DATE_FORMAT_CODE} ${Time.TIME_FORMAT_CODE}`
        )
        .format();

      const durationMinutes = this.selectedServiceDurationMinutes;
      this.model.end = moment
        .utc(this.model.start)
        .add(durationMinutes, "minute")
        .format();
    },
    showConfirmationModal() {
      this.isConfirmationModalVisible = true;
    },
    hideConfirmationModal() {
      this.isConfirmationModalVisible = false;
    },
    async allocateResource(booking) {
      const service = this.services[booking.serviceId];
      if (!service || !Array.isArray(service.resources) || !service.resources.length) {
        return null;
      }
      // Copy before shuffle — never mutate Vuex service.resources in place
      const resources = this.shuffle([...service.resources]);
      const startTime = moment.utc(booking.start).format("HH:mm");
      const startDate = moment.utc(booking.start).format("YYYY-MM-DD");
      for (const resource of resources) {
        const availableBookings = await bookings.getBookingsAvailability(
          resource,
          service._id
        );
        if (this.slotsDefined(availableBookings, startDate)) {
          if (availableBookings[startDate].includes(startTime)) {
            return resource;
          }
        }
      }
      return null;
    },
    slotsDefined(availableBookings, startDate) {
      return !!(
        availableBookings &&
        availableBookings[startDate] !== undefined
      );
    },
    rand(n) {
      return Math.floor(Math.random() * n);
    },
    swap(t, i, j) {
      const q = t[i];
      t[i] = t[j];
      t[j] = q;
      return t;
    },
    shuffle(resources) {
      let last = resources.length;
      let n;
      while (last > 0) {
        n = this.rand(last);
        this.swap(resources, n, --last);
      }
      return resources;
    },
    async submit() {
      this.showSavingIndicator();
      const booking = this.sanitize(this.model);
      try {
        if (booking.resourceId === "Any") {
          const allocated = await this.allocateResource(booking);
          if (!allocated) {
            this.$notify({
              type: "danger",
              title: "Error",
              text:
                "No available employee for this time slot. Please pick another time or staff member."
            });
            return;
          }
          booking.resourceId = allocated;
        }
        await this.createAdminBooking({ booking });
        this.backToBookings();
        this.$notify({
          type: "success",
          title: "Success",
          text: "Booking added."
        });
      } catch (e) {
        this.$notify({
          type: "danger",
          title: "Error",
          text: (e && e.message) || String(e)
        });
      } finally {
        this.hideSavingIndicator();
        this.hideConfirmationModal();
      }
    },
    showSavingIndicator() {
      this.isSaving = true;
    },
    hideSavingIndicator() {
      this.isSaving = false;
    },
    sanitize(booking) {
      const { total } = booking;
      return {
        ...booking,
        total: parseFloat(total)
      };
    },
    async forceSubmit(booking) {
      try {
        await this.createAdminBooking({ booking, override: true });
      } catch (e) {
        this.$notify({
          type: "danger",
          title: "Error",
          text: (e && e.message) || String(e)
        });
      }
    },
    onCancel() {
      this.$emit("cancel");
    },
    backToBookings() {
      this.goBack("/bookings");
    }
  },
  computed: {
    ...mapGetters({
      shopId: "shops/shopId",
      shop: "shops/shop",
      employees: "employees/employees",
      employeesList: "employees/employeesList",
      services: "services/services",
      servicesList: "services/servicesList",
      availableDates: "bookings/availableDates",
      availableTimesByDate: "bookings/availableTimesByDate"
    }),
    serviceOptions() {
      const { resourceId } = this.model;
      if (!resourceId) return [];
      if (resourceId === "Any") return this.servicesList;
      return this.servicesList.filter(
        service =>
          service &&
          Array.isArray(service.resources) &&
          service.resources.includes(resourceId)
      );
    },
    servicePackageOptions() {
      const { serviceId } = this.model;
      if (!serviceId) return [];
      const service = this.services[serviceId];
      if (!service) return [];
      const { packages, packagesDisplayOrder } = service;
      const order = Array.isArray(packagesDisplayOrder)
        ? packagesDisplayOrder
        : Object.keys(packages || {});
      return order.map(packageId => packages[packageId]).filter(Boolean);
    },
    listDateOptions() {
      const datesToShow = _.take(this.availableDates, this.numListDatesToShow);
      return datesToShow.map(date => ({
        value: date,
        text: moment(date, Time.DATE_FORMAT_CODE).format(
          Time.DATE_FORMAT_READABLE
        )
      }));
    },
    timeOptions() {
      const availableTimes = this.availableTimesByDate(this.selectedDate);
      return availableTimes.map(time => ({
        value: time,
        text: moment(time, Time.TIME_FORMAT_CODE).format(
          Time.TIME_FORMAT_READABLE
        )
      }));
    },
    timeOptionsMorning() {
      return this.timeOptions.filter(({ value }) => Time.isTimeMorning(value));
    },
    timeOptionsAfternoon() {
      return this.timeOptions.filter(({ value }) =>
        Time.isTimeAfternoon(value)
      );
    },
    timeOptionsEvening() {
      return this.timeOptions.filter(({ value }) => Time.isTimeEvening(value));
    },
    selectedEmployee() {
      const { model, employees } = this;
      const { resourceId } = model;
      if (!resourceId) return;
      return employees[resourceId];
    },
    selectedEmployeeName() {
      const { selectedEmployee } = this;
      if (!selectedEmployee) return this.$t("message.employee");
      return selectedEmployee.name;
    },
    selectedService() {
      const { model, services } = this;
      const { serviceId } = model;
      if (!serviceId) return;
      return services[serviceId];
    },
    /** Duration in minutes, honouring service durationUnit (HOURS vs MINUTES). */
    selectedServiceDurationMinutes() {
      const service = this.selectedService;
      if (!service) return 0;
      const duration = Number(service.duration) || 0;
      if (service.durationUnit === DURATION_UNITS.HOURS) {
        return duration * 60;
      }
      return duration;
    },
    selectedServicePackage() {
      const { selectedService, model } = this;
      if (!selectedService) return null;
      const { servicePackageId } = model;
      return (
        (selectedService.packages &&
          selectedService.packages[servicePackageId]) ||
        null
      );
    },
    preselectedDateFormatted() {
      const { preselectedDate } = this;
      if (!preselectedDate) return;
      return Time.formatDateReadable(preselectedDate);
    },
    selectedDate() {
      const { start } = this.model;
      if (!start) return;
      return moment.utc(start).format(Time.DATE_FORMAT_CODE);
    },
    selectedDateFormatted() {
      const { start } = this.model;
      if (!start) return;
      return moment
        .utc(start)
        .format(Time.DATE_FORMAT_READABLE);
    },
    selectedTime() {
      const { start } = this.model;
      if (!start) return;
      return moment.utc(start).format(Time.TIME_FORMAT_CODE);
    },
    endTime() {
      const { end } = this.model;
      if (!end) return;
      return moment.utc(end).format(Time.TIME_FORMAT_CODE);
    },
    selectedTimeFormatted() {
      const { start, end } = this.model;
      if (!start || !end) return;
      const startTime = moment.utc(start).format(Time.TIME_FORMAT_READABLE);
      const endTime = moment.utc(end).format(Time.TIME_FORMAT_READABLE);
      return `${startTime} - ${endTime}`;
    },
    totalFormatted() {
      const { total } = this.model;
      if (total === null || total === undefined || total === "") return;
      return `€${total}`;
    },
    canShowServiceSection() {
      return !!this.selectedEmployee;
    },
    canShowServicePackageSection() {
      return !!this.selectedService;
    },
    canShowDateSection() {
      if (!this.canShowServiceSection) return false;
      if (!this.selectedService) return false;
      if (!this.selectedServicePackage) return false;
      return true;
    },
    canShowTimeSection() {
      if (!this.canShowServiceSection) return false;
      if (!this.canShowDateSection) return false;
      if (!this.selectedDate) return false;
      return true;
    },
    canContinueToConfirmation() {
      if (!this.selectedEmployee) return false;
      if (!this.selectedService) return false;
      if (!this.selectedServicePackage) return false;
      if (!this.selectedDate) return false;
      if (!this.selectedTime) return false;
      if (!this.endTime) return false;
      return true;
    }
  },
  async created() {
    this.showLoadingIndicator();

    // Apply deep-link date before availability loads (used after package select)
    const { date, "populate-with": populateWithId } = this.$route.query;
    if (date) this.preselectedDate = date;

    try {
      let fetchEmployeesPromise;

      if (this.shop.resourceAnyMode === "Only Allow Any") {
        fetchEmployeesPromise = this.fetchAnyEmployee();
      } else if (this.shop.resourceAnyMode === "On") {
        fetchEmployeesPromise = this.fetchAllWithAnyEmployee();
      } else {
        fetchEmployeesPromise = this.fetchEmployees();
      }

      // Employees + services must load before book-again prefill
      await Promise.all([fetchEmployeesPromise, this.fetchServices()]);
      if (populateWithId) {
        await this.populateWith(populateWithId);
      }
    } catch (e) {
      this.$notify({
        type: "danger",
        title: this.$t("message.error"),
        text: this.$t("message.somethingWentWrong")
      });
      this.backToBookings();
    } finally {
      this.hideLoadingIndicator();
    }
  }
};
</script>
